import { useAuth } from "@/hooks/useAuth";
import { portalFetch, setErrorDetailsVisible } from "@/lib/portalApi";
import {
  buildTenantPath,
  deriveTenantCapabilities,
  deriveTenantRole,
  isTenantLoading,
} from "@/lib/tenantHelpers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useParams } from "react-router";

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  role: string;
  /** SIM-3115: what the server says this user may DO in this tenant. */
  capabilities: string[];
  createdAt: string;
}

interface TenantContextType {
  tenants: Tenant[];
  currentTenantId: string | null;
  currentTenantSlug: string | null;
  currentTenantName: string | null;
  currentRole: string | null;
  /** SIM-3115: server-supplied capability names for the current tenant. */
  currentCapabilities: string[];
  /** Navigate to a different tenant by slug */
  switchTenant: (slug: string) => void;
  isLoading: boolean;
  refetch: () => void;
  /** Build a path prefixed with the current tenant slug */
  tenantPath: (path: string) => string;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function buildSlugResolvedValue(args: {
  parent: TenantContextType;
  currentTenant: Tenant | null;
  tenantSlug: string | undefined;
  switchTenant: (slug: string) => void;
  tenantPath: (path: string) => string;
}): TenantContextType {
  const { parent, currentTenant, tenantSlug, switchTenant, tenantPath } = args;
  const currentId = currentTenant?.id ?? null;
  return {
    tenants: parent.tenants,
    currentTenantId: currentId,
    currentTenantSlug: currentTenant?.slug ?? tenantSlug ?? null,
    currentTenantName: currentTenant?.name ?? null,
    currentRole: deriveTenantRole(parent.tenants, currentId),
    currentCapabilities: deriveTenantCapabilities(parent.tenants, currentId),
    switchTenant,
    isLoading: parent.isLoading,
    refetch: parent.refetch,
    tenantPath,
  };
}

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [fetched, setFetched] = useState(false);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (authLoading || !user) return;
    setFetched(false);
    portalFetch<Tenant[]>("/api/portal/tenants")
      .then((data) => {
        setTenants(data);
      })
      .catch(() => setTenants([]))
      .finally(() => setFetched(true));
  }, [version, user, authLoading]);

  const isLoading = isTenantLoading(authLoading, !!user, fetched);

  // On global routes (outside TenantSlugResolver), derive the highest-privilege
  // role from the tenant list so super_admin routes can guard correctly.
  const globalRole = tenants.some((t) => t.role === "super_admin") ? "super_admin" : null;

  // SIM-2629: super admins see a technical details line on API errors. Off is the default.
  useEffect(() => {
    if (globalRole !== "super_admin") return undefined;
    setErrorDetailsVisible(true);
    return () => setErrorDetailsVisible(false);
  }, [globalRole]);

  const value: TenantContextType = {
    tenants,
    currentTenantId: null,
    currentTenantSlug: null,
    currentTenantName: null,
    currentRole: globalRole,
    currentCapabilities: [...new Set(tenants.flatMap((t) => t.capabilities ?? []))],
    switchTenant: () => {},
    isLoading,
    refetch,
    tenantPath: (path: string) => path,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

/**
 * Provides tenant context derived from the :tenantSlug URL param.
 * Must be rendered inside a route with :tenantSlug.
 */
export const TenantSlugResolver = ({ children }: { children: ReactNode }) => {
  const parent = useContext(TenantContext);
  if (!parent) throw new Error("TenantSlugResolver must be inside TenantProvider");

  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();

  const currentTenant = useMemo(
    () => parent.tenants.find((t) => t.slug === tenantSlug) ?? null,
    [parent.tenants, tenantSlug],
  );

  const switchTenant = useCallback(
    (slug: string) => {
      void navigate(`/${slug}`, { replace: true });
    },
    [navigate],
  );

  const resolvedSlug = currentTenant?.slug ?? tenantSlug;

  const tenantPath = useCallback(
    (path: string) => buildTenantPath(resolvedSlug, path),
    [resolvedSlug],
  );

  const value = buildSlugResolvedValue({
    parent,
    currentTenant,
    tenantSlug,
    switchTenant,
    tenantPath,
  });

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) throw new Error("useTenant must be used within TenantProvider");
  return context;
};
